import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import { doctorService, Doctor } from "../../services/doctor.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export function ViewDoctorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadDoctor();
    }
  }, [id]);

  const loadDoctor = async () => {
    try {
      setLoading(true);
      const data = await doctorService.getById(id!);
      setDoctor(data);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load doctor");
      navigate("/doctors");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await doctorService.delete(id!);
      showToast.success("Doctor deleted successfully");
      navigate("/doctors");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to delete doctor");
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Doctor not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/doctors")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Doctors
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {doctor.doctorName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Code: {doctor.doctorCode}
              {doctor.qualifications && ` | ${doctor.qualifications}`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/doctors/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Doctor Code</span>
              <span className="font-medium">{doctor.doctorCode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Doctor Name</span>
              <span className="font-medium">{doctor.doctorName}</span>
            </div>
            {doctor.qualifications && (
              <div className="flex justify-between">
                <span className="text-gray-500">Qualifications</span>
                <span className="font-medium">{doctor.qualifications}</span>
              </div>
            )}
            {doctor.specialization && (
              <div className="flex justify-between">
                <span className="text-gray-500">Specialization</span>
                <span className="font-medium">{doctor.specialization}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  doctor.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {doctor.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Registration Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Registration Details</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Registration Number</span>
              <span className="font-medium">{doctor.registrationNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Registration Body</span>
              <span className="font-medium">
                {doctor.registrationBody || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Registration Expiry</span>
              <span className="font-medium">
                {doctor.registrationExpiry
                  ? new Date(doctor.registrationExpiry).toLocaleDateString()
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Prescriptions</span>
              <span className="font-medium">
                {doctor._count?.prescriptions || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Invoices</span>
              <span className="font-medium">
                {doctor._count?.invoices || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Hospital/Clinic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Hospital / Clinic</h2>
          <div className="space-y-3">
            {doctor.hospital ? (
              <div className="flex justify-between">
                <span className="text-gray-500">Hospital</span>
                <span className="font-medium">
                  {doctor.hospital.hospitalName}
                </span>
              </div>
            ) : doctor.clinicName ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Clinic Name</span>
                  <span className="font-medium">{doctor.clinicName}</span>
                </div>
                {doctor.clinicAddress && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Clinic Address</span>
                    <span className="font-medium text-right max-w-xs">
                      {doctor.clinicAddress}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-500">
                No hospital or clinic assigned
              </div>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Phone</span>
              <span className="font-medium">{doctor.phone || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Email</span>
              <span className="font-medium">{doctor.email || "-"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Doctor"
        message={`Are you sure you want to delete "${doctor.doctorName}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default ViewDoctorPage;
